import * as React from 'react';
import { Button, Modal, ModalVariant, PageSection, Title } from '@patternfly/react-core';
import ProducerInterface from '@app/Interface/ProducerInterface';
import { UserContext } from '@app/Context/UserContext';
import UserAuthentication from '@app/Security/UserAuthentication';
import User from '@app/Interface/UserInterface';
import UserPermissionsTable from '@app/Components/User/UserPermissionsTable';
import ItemLoader from '@app/Components/ItemLoader';
import UserRoleTypesInterface from '@app/Interface/UserRoleTypesInterface';
import UserRoleDescriptions from '@app/Components/User/UserRoleDescriptions';
import MyFpuComponentViewInterface from '@app/Interface/MyFpuComponentViewInterface';
import MyFpuComponentViewModel from '@app/Model/MyFpuComponentViewModel';
import FpuPermissionsComponentTypeModel from '@app/Model/FpuPermissionsComponentViewModel';
import FpuPermissionsComponentTypeInterface from '@app/Interface/FpuPermissionsComponentViewInterface';
import FpuAddUserPermissions from './FpuAddUserPermisions';
import FpuRevokeUserPermissions from './FpuRevokeUserPermissions';
import FpuUpdateUserPermissions from './FpuUpdateUserPermissions';
import ToastNotificationInterface from '@app/Interface/ToastNotificationInterface';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import ToastNotificationModel from '@app/Model/ToastNotificationModel';
import UserModel from '@app/Model/UserModel';

interface FpuPermissionsProps {
    producerId: number;
    producer: ProducerInterface;
    roleTypes: UserRoleTypesInterface[];
    buttonHandler: (component: MyFpuComponentViewInterface) => void;
}

const FpuPermissions: React.FC<FpuPermissionsProps> = ({ producerId, producer, roleTypes, buttonHandler }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const { userState } = React.useContext(UserContext);

    const defaultComponentView = new FpuPermissionsComponentTypeModel();

    const [componentView, setComponentView] = React.useState<FpuPermissionsComponentTypeInterface>(defaultComponentView);

    const [isLoadingUsers, setIsLoadingUsers] = React.useState(true);

    const [users, setUsers] = React.useState<User[]>([]);

    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const [refetchUsers, setRefetchUsers] = React.useState(false);

    const [toastNotificationData, setToastNotificationData] = React.useState<ToastNotificationInterface>(new ToastNotificationModel());

    const handleModalToggle = () => {
        setIsModalOpen(!isModalOpen);
    }

    const changeView = (component: FpuPermissionsComponentTypeInterface, fetchUsers?: boolean, toastData?: ToastNotificationInterface) => {

        setComponentView(component);

        if (fetchUsers) {
            setRefetchUsers(true);
        }

        if (toastData) {
            setToastNotificationData(toastData);
        }

    }

    // check for toast data
    React.useEffect(() => {

        if (toastNotificationData.title.length > 0) {

            CreateToastNotification(toastDispatch, toastNotificationData.type, toastNotificationData.title, toastNotificationData.description);

        }

    }, [toastDispatch, toastNotificationData.description, toastNotificationData.title, toastNotificationData.type]);

    // get the users associated with this FPU
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function getUsersForFpu() {

            const usersForFpu = await UserAuthentication.getUsers(controller, Number(producerId));

            if (mounted) {
                setUsers(usersForFpu);
            }

            setIsLoadingUsers(false);

            setRefetchUsers(false);
        }

        if (producerId || refetchUsers) {

            setIsLoadingUsers(true);

            getUsersForFpu();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [producerId, refetchUsers]);

    if (componentView.component === "FpuAddUserPermissions") {
        return (
            <FpuAddUserPermissions producerId={componentView.fpuId} producer={componentView.producer} roleTypes={roleTypes} changeView={changeView} />
        );
    }

    if (componentView.component === "FpuRevokeUserPermissions") {
        return (
            <FpuRevokeUserPermissions producer={producer} userId={componentView.userId} userToUpdate={componentView.user} changeView={changeView} />
        );
    }

    if (componentView.component === "FpuUpdateUserPermissions") {
        return (
            <FpuUpdateUserPermissions producerId={componentView.fpuId} producer={componentView.producer} roleTypes={roleTypes} userToUpdate={componentView.user} changeView={changeView} />
        );
    }


    const viewFpuList = new MyFpuComponentViewModel();

    return (
        <PageSection>
            <Button
                variant="primary"
                onClick={() => buttonHandler(viewFpuList)}
            >
                Return to FPU List
            </Button>
            <Title headingLevel="h1" className="pf-u-mb-md">FPU Permissions</Title>
            <Title headingLevel="h2">{producer.alphaName} - {producerId}</Title>
            <div className="pf-u-mb-md">
                <p>Every user has a “role” and their role effects which parts of the the site they can access.</p>
                <Button variant="link" onClick={handleModalToggle} className="pf-u-p-0">Learn more about permissions</Button>
                <Modal
                    variant={ModalVariant.medium}
                    title="Permissions"
                    isOpen={isModalOpen}
                    onClose={handleModalToggle}
                    actions={[
                        <Button key="ok" variant="primary" onClick={handleModalToggle}>
                            OK
                        </Button>
                    ]}><UserRoleDescriptions user={userState.user} roleTypes={roleTypes} /></Modal>
            </div>
            <Button
                variant="secondary"
                onClick={() => changeView(new FpuPermissionsComponentTypeModel(producerId, producer, 0, new UserModel(), "FpuAddUserPermissions"))}
                className="pf-u-mb-md"
            >
                Invite user
            </Button>
            {isLoadingUsers ? <ItemLoader /> : <UserPermissionsTable producerId={producerId} producer={producer} currentUser={userState.user} users={users} roleTypes={roleTypes} changeView={changeView} />}
        </PageSection>);

};

export default FpuPermissions;